import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Separator } from "@/components/ui/separator"
import { useAppDispatch, useAppSelector } from "hooks/storeHooks"
import { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { getTintVTOService } from "services/tintvto"
import { setUserStatus } from "store/user/userSlice"
import { normalizeMerchantStatus } from "utils/userStatus"
import ActivateAccountHubspotForm from "./ActivateAccountHubspotForm"

export const ActivateAccount = () => {
  const { t } = useTranslation()
  const service = getTintVTOService()
  const dispatch = useAppDispatch()
  const userInfo = useAppSelector((state) => state.user.userInfo)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const handleHubspotSubmitClick = useCallback(() => {
    setIsModalOpen(false)
  }, [])

  const getMerchantStatus = async () => {
    if (!userInfo?.merchantId) return

    // Platform-native: the account status lives on /admin/me; re-reading it
    // unlocks the page as soon as support activates the account.
    try {
      const me = await service.getCurrentAdmin()
      if (me?.merchant?.status) dispatch(setUserStatus(normalizeMerchantStatus(me.merchant.status)))
    } catch {
      // Supplementary refresh; the page keeps its current state on failure.
    }
  }

  useEffect(() => {
    setIsModalOpen(true)
    void getMerchantStatus()
  }, [])

  return (
    <div
      className="flex min-h-screen w-full items-center justify-center bg-muted/30 p-4"
      data-testid="activate-account-page"
    >
      <Dialog open={isModalOpen} onOpenChange={() => undefined}>
        <DialogContent
          className="w-[min(95vw,820px)] max-w-none gap-0 p-0 [&>[data-slot=dialog-close]]:hidden"
          data-testid="activate-account-dialog"
        >
          <DialogHeader className="px-6 pt-5 pb-4 sm:px-8">
            <DialogTitle className="flex items-center gap-3 text-lg">
              <img src="/tint-logo-small.svg" alt="" className="size-10 rounded-lg" />
              {t("activateAccount.supportTeamTitle")}
            </DialogTitle>
            <DialogDescription className="sr-only">
              {t("activateAccount.assistanceDescription")}
            </DialogDescription>
          </DialogHeader>

          <Separator />

          <div className="px-6 py-6 sm:px-8 sm:py-8">
            <div className="mx-auto flex w-full max-w-[680px] flex-col gap-6">
              <div className="space-y-3 text-center">
                <h3 className="text-xl font-semibold">
                  {t("activateAccount.assistanceNeededTitle")}
                </h3>
                <p className="text-sm leading-6 text-muted-foreground">
                  {t("activateAccount.assistanceDescription")}
                </p>
              </div>

              <ActivateAccountHubspotForm onSubmit={handleHubspotSubmitClick} />

              <p className="text-center text-sm text-muted-foreground">
                {t("activateAccount.or")}{" "}
                <a
                  href="https://www.banuba.com/support-tint"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-primary underline underline-offset-4"
                >
                  {t("activateAccount.contactSupport")}
                </a>{" "}
                {t("activateAccount.team")}
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {!isModalOpen && (
        <Card className="w-full max-w-[460px]" data-testid="activate-account-success">
          <CardHeader className="items-center text-center">
            <CardTitle className="text-2xl">{t("activateAccount.successTitle")}</CardTitle>
            <CardDescription>{t("activateAccount.successDescription")}</CardDescription>
          </CardHeader>
          <CardContent className="text-center text-sm text-muted-foreground">
            {t("activateAccount.team")}
          </CardContent>
        </Card>
      )}
    </div>
  )
}

export default ActivateAccount
